import { useMemo, useState } from "react";
import { Archive, Pencil, Search, Trash2, X } from "lucide-react";
import type { Payee } from "../../types";
import { nameKey } from "../../lib/names";
import PayeeAvatar from "../../components/PayeeAvatar";
import PayeeAlphabet from "./PayeeAlphabet";
import { payeeHeadingId } from "./use-payee-alphabet";

const COLLATOR = new Intl.Collator(undefined, { sensitivity: "base", numeric: true });
const searchKey = (value: string) => nameKey(value).normalize("NFKD").replace(/\p{M}/gu, "");

export default function PayeeDirectory({ payees, busy, countLabel, used, onOpen, onEdit, onRemove }: {
  payees: Payee[];
  busy: boolean;
  countLabel: (id: string) => string;
  used: (id: string) => boolean;
  onOpen: (id: string) => void;
  onEdit: (payee: Payee) => void;
  onRemove: (payee: Payee) => void;
}) {
  const [query, setQuery] = useState("");
  const { groups, total, count } = useMemo(() => {
    const active = payees.filter(p => p.active !== false);
    const needle = searchKey(query);
    const matches = active.filter(p => searchKey(`${p.name} ${p.notes || ""}`).includes(needle))
      .sort((a, b) => COLLATOR.compare(a.name, b.name) || a.id.localeCompare(b.id));
    const sections = new Map<string, Payee[]>();
    for (const payee of matches) {
      const first = searchKey(payee.name).charAt(0).toUpperCase();
      const letter = /^[A-Z]$/.test(first) ? first : "#";
      const group = sections.get(letter) || [];
      group.push(payee);
      sections.set(letter, group);
    }
    const groups = [...sections].sort(([a], [b]) => a === "#" ? 1 : b === "#" ? -1 : a.localeCompare(b));
    return { groups, total: active.length, count: matches.length };
  }, [payees, query]);

  return (
    <div className="payee-directory">
      <div className="search-field payee-directory-search">
        <Search size={18} aria-hidden="true" />
        <input type="search" aria-label="Search payees" placeholder="Search names or notes"
          value={query} onChange={e => setQuery(e.target.value)} />
        {query && <button type="button" className="icon-button" aria-label="Clear payee search" onClick={() => setQuery("")}><X size={17} /></button>}
      </div>
      <p className="payee-directory-count muted" role="status">
        {query.trim() ? `${count} of ${total}` : total} {total === 1 ? "payee" : "payees"}
      </p>
      {count ? (
        <div className="payee-directory-body">
          <div className="payee-directory-groups">
            {groups.map(([letter, entries]) => (
              <section key={letter} className="managed-account-owner payee-directory-group" aria-labelledby={payeeHeadingId(letter)}>
                <h3 id={payeeHeadingId(letter)} tabIndex={-1}>{letter}</h3>
                <div className="managed-account-list">
                  {entries.map(payee => {
                    const isUsed = used(payee.id);
                    const label = countLabel(payee.id);
                    return (
                      <article key={payee.id} className="managed-account">
                        <button className="payee-open" aria-label={`Open ${payee.name}`} onClick={() => onOpen(payee.id)}>
                          <PayeeAvatar id={payee.id} name={payee.name} size={30} />
                          <span><strong>{payee.name}</strong>{label && <small>{label}</small>}</span>
                        </button>
                        <div>
                          <button aria-label={`Edit ${payee.name}`} onClick={() => onEdit(payee)}><Pencil size={17} /></button>
                          <button aria-label={`${isUsed ? "Archive" : "Delete"} ${payee.name}`} disabled={busy} onClick={() => onRemove(payee)}>
                            {isUsed ? <Archive size={17} /> : <Trash2 size={17} />}
                          </button>
                        </div>
                      </article>
                    );
                  })}
                </div>
              </section>
            ))}
          </div>
          <PayeeAlphabet key={groups.map(([letter]) => letter).join("")} letters={groups.map(([letter]) => letter)} />
        </div>
      ) : <p className="muted empty-small">{total ? "No payees match your search." : "No payees yet. Add someone to link them to transactions or an IOU."}</p>}
    </div>
  );
}
